import React, { useState } from 'react';
import api from '../../services/api';
import { useMessage } from '../../components/Message';

const ImageUploadModal = ({ game, onClose, onUploadComplete }) => {
    const [images, setImages] = useState({ 
        banner: null, 
        logo: null, 
        screenshots: [] 
    });
    const [uploading, setUploading] = useState(false);
    const [previews, setPreviews] = useState({ 
        banner: null, 
        logo: null, 
        screenshots: [] 
    });
    const { showMessage } = useMessage();

    const handleFileSelect = (type, files) => {
        const fileArray = Array.from(files);
        
        if (type === 'screenshots') {
            const selected = fileArray.slice(0, 3);
            setImages(prev => ({ ...prev, screenshots: selected }));
            
            // Create preview URLs
            const previewUrls = selected.map(file => URL.createObjectURL(file));
            setPreviews(prev => ({ ...prev, screenshots: previewUrls }));
        } else {
            const file = files[0];
            if (file) {
                setImages(prev => ({ ...prev, [type]: file }));
                setPreviews(prev => ({ 
                    ...prev, 
                    [type]: URL.createObjectURL(file) 
                }));
            }
        }
    };

    const handleRemoveFile = (type, index = null) => {
        if (type === 'screenshots') {
            if (index !== null) {
                // Remove specific screenshot
                const newScreenshots = [...images.screenshots];
                const newPreviews = [...previews.screenshots];
                newScreenshots.splice(index, 1);
                newPreviews.splice(index, 1);
                setImages(prev => ({ ...prev, screenshots: newScreenshots }));
                setPreviews(prev => ({ ...prev, screenshots: newPreviews }));
            }
        } else {
            setImages(prev => ({ ...prev, [type]: null }));
            setPreviews(prev => ({ ...prev, [type]: null }));
        }
    };

    const handleUpload = async () => {
        const formData = new FormData();
        
        if (images.banner) {
            formData.append('banner', images.banner);
        }
        if (images.logo) {
            formData.append('logo', images.logo);
        }
        
        images.screenshots.forEach((screenshot, index) => {
            if (index < 3) {
                formData.append(`screenshot${index + 1}`, screenshot);
            }
        });

        try {
            setUploading(true);
            await api.post(`/admin/games/${game.gameId}/images`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    // You could add a progress bar here if needed
                }
            });
            showMessage('Images uploaded successfully!', 'success');
            onUploadComplete();
            onClose();
        } catch (error) {
            showMessage(error.response?.data?.message || 'Upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    // Cleanup preview URLs on unmount
    React.useEffect(() => {
        return () => {
            Object.values(previews).forEach(preview => {
                if (preview && typeof preview === 'string' && preview.startsWith('blob:')) {
                    URL.revokeObjectURL(preview);
                }
            });
            previews.screenshots.forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, []);

    const hasFiles = images.banner || images.logo || images.screenshots.length > 0;

    return (
        <div className="admin-modal-overlay" onClick={onClose}>
            <div className="admin-modal admin-image-upload-modal" onClick={(e) => e.stopPropagation()}>
                <div className="admin-modal-header">
                    <h2 className="admin-modal-title">
                        <span className="admin-modal-icon">🖼️</span>
                        Upload Images for <span className="admin-game-name">{game.name}</span>
                    </h2>
                    <button className="admin-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="admin-modal-body">
                    <div className="admin-upload-grid">
                        {/* Banner Upload */}
                        <div className="admin-upload-card">
                            <div className="admin-upload-card-header">
                                <span className="admin-upload-card-icon">📸</span>
                                <h4>Banner Image</h4>
                                <span className="admin-upload-required">Required</span>
                            </div>
                            <div className="admin-upload-card-body">
                                {previews.banner ? (
                                    <div className="admin-preview-container">
                                        <img 
                                            src={previews.banner} 
                                            alt="Banner preview" 
                                            className="admin-preview-image"
                                        />
                                        <button 
                                            className="admin-preview-remove"
                                            onClick={() => handleRemoveFile('banner')}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <div className="admin-upload-dropzone">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFileSelect('banner', e.target.files)}
                                            id="banner-upload"
                                            className="admin-file-input-hidden"
                                        />
                                        <label htmlFor="banner-upload" className="admin-upload-label">
                                            <span className="admin-upload-icon">📤</span>
                                            <span>Click to upload</span>
                                            <span className="admin-upload-hint">Recommended: 800x400</span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Logo Upload */}
                        <div className="admin-upload-card">
                            <div className="admin-upload-card-header">
                                <span className="admin-upload-card-icon">🎯</span>
                                <h4>Logo Image</h4>
                                <span className="admin-upload-optional">Optional</span>
                            </div>
                            <div className="admin-upload-card-body">
                                {previews.logo ? (
                                    <div className="admin-preview-container">
                                        <img 
                                            src={previews.logo} 
                                            alt="Logo preview" 
                                            className="admin-preview-image admin-preview-logo"
                                        />
                                        <button 
                                            className="admin-preview-remove"
                                            onClick={() => handleRemoveFile('logo')}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <div className="admin-upload-dropzone">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFileSelect('logo', e.target.files)}
                                            id="logo-upload"
                                            className="admin-file-input-hidden"
                                        />
                                        <label htmlFor="logo-upload" className="admin-upload-label">
                                            <span className="admin-upload-icon">📤</span>
                                            <span>Click to upload</span>
                                            <span className="admin-upload-hint">Recommended: 200x200</span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Screenshots Upload */}
                    <div className="admin-upload-card admin-screenshots-card">
                        <div className="admin-upload-card-header">
                            <span className="admin-upload-card-icon">🖼️</span>
                            <h4>Screenshots</h4>
                            <span className="admin-upload-optional">Optional (Max 3)</span>
                        </div>
                        <div className="admin-upload-card-body">
                            {previews.screenshots.length > 0 ? (
                                <div className="admin-screenshots-grid">
                                    {previews.screenshots.map((url, index) => (
                                        <div key={index} className="admin-screenshot-item">
                                            <img 
                                                src={url} 
                                                alt={`Screenshot ${index + 1}`} 
                                                className="admin-screenshot-preview"
                                            />
                                            <button 
                                                className="admin-screenshot-remove"
                                                onClick={() => handleRemoveFile('screenshots', index)}
                                            >
                                                ✕
                                            </button>
                                            <span className="admin-screenshot-number">{index + 1}</span>
                                        </div>
                                    ))}
                                    {previews.screenshots.length < 3 && (
                                        <div className="admin-screenshot-add">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={(e) => handleFileSelect('screenshots', e.target.files)}
                                                id="screenshots-upload"
                                                className="admin-file-input-hidden"
                                            />
                                            <label htmlFor="screenshots-upload" className="admin-upload-label admin-upload-screenshot">
                                                <span className="admin-upload-icon">➕</span>
                                                <span>Add more</span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="admin-upload-dropzone admin-upload-screenshots">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => handleFileSelect('screenshots', e.target.files)}
                                        id="screenshots-upload"
                                        className="admin-file-input-hidden"
                                    />
                                    <label htmlFor="screenshots-upload" className="admin-upload-label admin-upload-label-large">
                                        <span className="admin-upload-icon">📤</span>
                                        <span>Upload Screenshots</span>
                                        <span className="admin-upload-hint">Select up to 3 images</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upload Status */}
                    {uploading && (
                        <div className="admin-upload-progress">
                            <div className="admin-progress-bar">
                                <div className="admin-progress-fill admin-progress-animated"></div>
                            </div>
                            <p className="admin-progress-text">Uploading images... Please wait</p>
                        </div>
                    )}
                </div>

                <div className="admin-modal-footer">
                    <div className="admin-modal-actions">
                        <button 
                            className="admin-btn-upload"
                            onClick={handleUpload}
                            disabled={uploading || !hasFiles}
                        >
                            {uploading ? (
                                <>
                                    <span className="admin-spinner"></span>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <span>📤</span>
                                    Upload Images
                                </>
                            )}
                        </button>
                        <button 
                            className="admin-btn-cancel"
                            onClick={onClose}
                            disabled={uploading}
                        >
                            Cancel
                        </button>
                    </div>
                    <div className="admin-upload-info">
                        <span className="admin-info-icon">ℹ️</span>
                        <span>Supported formats: JPG, PNG, GIF, WebP</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageUploadModal;